import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

import { businessEmailValidation } from "../../../../utils/validation/businessEmail";
import { ArrowLeft, ArrowRight, FileText } from "lucide-react";
import { Checkbox as MuiCheckbox } from "@mui/material";

import { UIButton, UIPhoneField, UITextField } from "../../../../components/ui";
import PasswordPairWithRules from "../../../../components/PasswordPairWithRules";
import messages from "../../../../assets/locale/messages";
import { createCompanyUser } from "../../../../network/apis/companyPortalRegistration";
import { generateDeviceId, setDeviceId } from "../../../../utils/Auth";
import { getFirebaseToken } from "../../../../firebase/helpers";
import { showErrorMsg } from "../../../../utils/Helpers";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { persistCompanyPortalSession } from "../../../../utils/companyRegistration";
import {
  passwordHasCharsetRules,
  passwordHasMinLength,
} from "../../../../components/PasswordPairWithRules/passwordRules";

const termsArabic = `${process.env.PUBLIC_URL}/terms-and-conditions/Yoram_Terms_Arabic.pdf`;
const termsEnglish = `${process.env.PUBLIC_URL}/terms-and-conditions/Yoram_Terms_English.pdf`;

import "./ContactForm.scss";
import { PASSWORD_REGEX } from "../../../../utils/Patterns";

/** Full international digits + stored country dial (digits only, no +) → API fields. */
const splitPhoneForApi = (fullDigits, countryDialDigits) => {
  const digits = String(fullDigits ?? "").replace(/\D/g, "");
  const dial = String(countryDialDigits ?? "966").replace(/\D/g, "");
  const national = digits.startsWith(dial) ? digits.slice(dial.length) : digits;
  const dial_code = dial.startsWith("+") ? dial : `+${dial}`;
  return { dial_code, phone_number: national };
};

/**
 * Contact Info step — creates company portal user (unverified).
 */
const ContactForm = () => {
  const history = useHistory();
  const lang = useSelector((state) => state.locale.lang);
  const { contact: t, inputsValidations: iv, resetPassword: rp } =
    messages[lang];

  const validationSchema = useMemo(
    () =>
      Yup.object({
        contactName: Yup.string().trim().required("nameRequired"),
        phone: Yup.string()
          .transform((v) =>
            v == null ? "" : String(v).replace(/\D/g, "")
          )
          .required("generalFieldRequired")
          .test("phone", "invalidPhone", (val) => {
            if (!val) return false;
            const d = String(val).replace(/\D/g, "");
            if (!/^[1-9]\d{7,14}$/.test(d)) return false;
            if (d.startsWith("966")) {
              const national = d.slice(3).replace(/^0/, "");
              return /^5\d{8}$/.test(national);
            }
            return true;
          }),
        email: businessEmailValidation,
        password: Yup.string()
          .required("passwordRequired")
          .matches(PASSWORD_REGEX, ""),
        confirmPassword: Yup.string()
          .required("confirmNewPasswordRequired")
          .oneOf([Yup.ref("password")], "passwordIsNotMatched"),
        terms: Yup.boolean().oneOf([true], "termsRequired"),
      }),
    []
  );

  const {
    values,
    errors,
    touched,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    setValues,
    handleSubmit,
    isSubmitting,
    isValid,
    dirty,
  } = useFormik({
    initialValues: {
      contactName: "",
      phone: "",
      /** Country calling code digits only (no +), from PhoneInput `countryData.dialCode` — default SA. */
      phoneCountryDialCode: "966",
      email: "",
      password: "",
      confirmPassword: "",
      terms: false,
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (formValues, { setSubmitting }) => {
      try {
        const device_id = generateDeviceId();
        const { dial_code, phone_number } = splitPhoneForApi(
          formValues.phone,
          formValues.phoneCountryDialCode,
        );
        const data = await createCompanyUser({
          email: formValues.email.trim(),
          password: formValues.password,
          name: formValues.contactName.trim(),
          phone_number,
          dial_code,
          device_id,
          fcm_token: getFirebaseToken() || "",
        });
        // `sessionPortal` must be set first — `persistCompanyPortalSession` writes to the
        // storage bucket it resolves, and would otherwise land in `admin_user`.
        localStorage.setItem("userType", "client");
        localStorage.setItem("sessionPortal", "company");
        persistCompanyPortalSession(data);
        setDeviceId(device_id);
        history.push(ROUTE_PATHS.companySignupOtp);
      } catch (error) {
        let msg = error.response?.data?.message ?? error.message ?? "";
        if (Array.isArray(msg)) {
          msg = msg.filter(Boolean).join(", ");
        }
        if (typeof msg !== "string") {
          msg = String(msg);
        }
        const normalized = msg.trim();
        if (normalized) {
          showErrorMsg(normalized);
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const termsFile = lang === "ar" ? termsArabic : termsEnglish;

  const lenOk = passwordHasMinLength(values.password);
  const charsetOk = passwordHasCharsetRules(values.password);

  const showConfirmMatch = Boolean(
    touched.confirmPassword &&
      values.confirmPassword &&
      values.password === values.confirmPassword &&
      !errors.confirmPassword
  );

  return (
    <div className="contact-form-step w-100">
      <div>
        <h2 className="step-title">{t.title}</h2>
        <p className="step-description mb-0">{t.subtitle}</p>
      </div>

      <form
        className="step-form-content"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="row">
          <div className="col-12 col-md-6">
            <UITextField
              id="contact-name"
              name="contactName"
              label={t.name.label}
              placeholder={t.name.placeholder}
              type="text"
              value={values.contactName}
              onValueChange={(value) => {
                setFieldTouched("contactName");
                setFieldValue("contactName", value);
              }}
              onBlur={handleBlur}
              error={Boolean(touched.contactName && errors.contactName)}
              helperText={touched.contactName ? iv[errors.contactName] : ""}
            />
          </div>
          <div className="col-12 col-md-6">
            <UIPhoneField
              name="phone"
              label={t.phone.label}
              placeholder={t.phone.placeholder}
              country="sa"
              preferredCountries={["eg", "sa", "ae"]}
              value={values.phone}
              onChange={(fullValue, countryData) => {
                const dc = countryData?.dialCode;
                // Formik validates each `setFieldValue` against the pre-update `values`
                // snapshot, so two calls in the same keystroke race and can leave a stale
                // error on screen until the next validation (e.g. blur). Batch both fields
                // into one `setValues` call so a keystroke only triggers one validation.
                setValues((prev) => ({
                  ...prev,
                  phone: fullValue,
                  phoneCountryDialCode:
                    dc != null && dc !== ""
                      ? String(dc).replace(/\D/g, "")
                      : prev.phoneCountryDialCode,
                }));
                setFieldTouched("phone", true, false);
              }}
              onBlur={handleBlur}
              formValueIsFullInternational
              error={Boolean(touched.phone && errors.phone)}
              helperText={touched.phone ? iv[errors.phone] : ""}
            />
          </div>
        </div>

        <div className="col-12">
          <UITextField
            id="contact-email"
            name="email"
            label={t.email.label}
            placeholder={t.email.placeholder}
            type="email"
            value={values.email}
            onValueChange={(value) => {
              setFieldTouched("email");
              setFieldValue("email", value);
            }}
            onBlur={handleBlur}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email ? iv[errors.email] : ""}
          />
        </div>

        <PasswordPairWithRules
          passwordId="contact-password"
          confirmId="contact-confirm-password"
          passwordName="password"
          confirmName="confirmPassword"
          passwordLabel={t.password.label}
          confirmLabel={t.confirmPassword.label}
          passwordPlaceholder={t.password.placeholder}
          confirmPlaceholder={t.confirmPassword.placeholder}
          passwordValue={values.password}
          confirmValue={values.confirmPassword}
          onPasswordChange={(value) => {
            setFieldTouched("password", true);
            setFieldValue("password", value);
          }}
          onConfirmChange={(value) => {
            setFieldTouched("confirmPassword", true);
            setFieldValue("confirmPassword", value);
          }}
          onBlur={handleBlur}
          passwordRequired
          passwordError={errors.password}
          confirmError={errors.confirmPassword}
          passwordTouched={touched.password}
          confirmTouched={touched.confirmPassword}
          rules={[
            {
              label: rp.step2.rules.length,
              satisfied:
                values.password.length > 0 ? lenOk : undefined,
            },
            {
              label: rp.step2.rules.complexity,
              satisfied:
                values.password.length > 0 ? charsetOk : undefined,
            },
          ]}
          showExternalConfirmMatch
          confirmMatchVisible={showConfirmMatch}
          confirmMatchAriaLabel={rp.step2.confirmMatchAria}
        />

        <div className="col-12">
          <div className="contact-form-step__terms-card">
            <div
              className="contact-form-step__terms-row"
              role="group"
              aria-labelledby="contact-terms-heading"
            >
              <div className="contact-form-step__terms-doc-wrap">
                <FileText
                  className="contact-form-step__terms-doc-icon"
                  size={24}
                  aria-hidden
                />
              </div>
              <div className="contact-form-step__terms-body">
                <p
                  className="contact-form-step__terms-card-title mb-1"
                  id="contact-terms-heading"
                >
                  <a
                    className="contact-form-step__terms-title-link"
                    href={termsFile}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t.terms.title}
                  </a>
                </p>
                <p className="contact-form-step__terms-confirm mb-0">
                  {t.terms.confirmation}
                  <a
                    className="contact-form-step__terms-link"
                    href={termsFile}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t.terms.link}
                  </a>
                </p>
              </div>
              <div className="contact-form-step__terms-check-wrap">
                <MuiCheckbox
                  id="contact-terms"
                  name="terms"
                  checked={values.terms}
                  onChange={(e) => {
                    setFieldTouched("terms");
                    setFieldValue("terms", e.target.checked);
                  }}
                  color="primary"
                  disableRipple
                  className="contact-form-step__terms-mui-checkbox"
                  inputProps={{
                    "aria-label": t.terms.checkboxAria,
                  }}
                />
              </div>
            </div>
            {touched.terms && errors.terms ? (
              <p className="text-error small mb-0 mt-2" role="alert">
                {iv[errors.terms]}
              </p>
            ) : null}
          </div>
        </div>

        <div className="contact-form-step__actions d-flex flex-column flex-md-row gap-3 align-items-stretch">
          <div className="contact-form-step__next-wrap flex-grow-1">
            <UIButton
              type="submit"
              variant="primary"
              size="large"
              fullWidth
              disabled={!dirty || !isValid || isSubmitting}
              endIcon={
                lang === "ar" ? <ArrowLeft size={16} /> : <ArrowRight size={16} />
              }
            >
              {t.actions.next}
            </UIButton>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
